import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Heart, Mail, Lock, Eye, EyeOff, User, AlertCircle, Loader2 } from 'lucide-react';
import { trackSignup, trackLogin } from '../utils/analytics';
import { BACKEND_URL } from '../utils/config';

const AuthPage = () => {
  const { t } = useLanguage();
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') || 'signin';
  const [activeTab, setActiveTab] = useState(initialTab);
  
  // Sign In State
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  
  // Sign Up State
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [signupError, setSignupError] = useState('');
  const [signupLoading, setSignupLoading] = useState(false);
  const [isEmailExists, setIsEmailExists] = useState(false);
  
  // Common State
  const [showPassword, setShowPassword] = useState(false);
  const [googleEnabled, setGoogleEnabled] = useState(true);
  
  const { login, register } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const checkOAuthConfig = async () => {
      try {
        const googleRes = await fetch(`${BACKEND_URL}/api/auth/google/config`);
        const googleData = await googleRes.json();
        setGoogleEnabled(googleData.enabled);
      } catch (err) {
        console.log('OAuth config check failed');
      }
    };
    checkOAuthConfig();
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    setLoginLoading(true);

    try {
      const loginResponse = await login(loginEmail, loginPassword);
      const userRole = loginResponse?.role || 'user';
      
      // Track successful login
      trackLogin('email');
      
      // Check for redirect URL parameter
      const redirectUrl = searchParams.get('redirect');
      const pendingAction = searchParams.get('pending');
      
      if (redirectUrl) {
        // If there's a pending campaign submission, set flag for auto-submit
        if (pendingAction === 'campaign') {
          localStorage.setItem('pendingCampaignSubmit', 'true');
        }
        // Redirect to the specified page (e.g., /create)
        navigate('/' + redirectUrl);
      } else if (userRole === 'super_admin') {
        navigate('/super-admin');
      } else if (userRole === 'platform_admin') {
        navigate('/platform-admin');
      } else if (userRole === 'admin') {
        navigate('/admin');
      } else {
        navigate('/dashboard');
      }
    } catch (err) {
      const errorDetail = err.response?.data?.detail;
      let errorMessage = 'Invalid email or password';
      if (typeof errorDetail === 'string') {
        errorMessage = errorDetail;
      }
      setLoginError(errorMessage);
    } finally {
      setLoginLoading(false);
    }
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    setSignupError('');
    setIsEmailExists(false);

    if (password !== confirmPassword) {
      setSignupError('Passwords do not match');
      return;
    }

    if (password.length < 6) {
      setSignupError('Password must be at least 6 characters');
      return;
    }

    setSignupLoading(true);

    try {
      // Use email username as default name, user can update in profile
      const defaultName = email.split('@')[0];
      await register(defaultName, email, password);
      
      // Track successful signup
      trackSignup('email');
      
      // Check for redirect URL parameter and pending action
      const redirectUrl = searchParams.get('redirect');
      const pendingAction = searchParams.get('pending');
      
      if (redirectUrl) {
        // For signup, store redirect URL and pending action
        localStorage.setItem('postVerifyRedirect', redirectUrl);
        if (pendingAction === 'campaign') {
          localStorage.setItem('pendingCampaignSubmit', 'true');
        }
      }
      navigate('/verify-email');
    } catch (err) {
      const errorDetail = err.response?.data?.detail;
      
      if (errorDetail === 'EMAIL_EXISTS' || err.response?.status === 409) {
        setIsEmailExists(true);
      } else {
        setSignupError('Registration failed. Please try again.');
      }
    } finally {
      setSignupLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    if (!googleEnabled) {
      alert('Google Sign In is not yet configured. Please use email login or contact support.');
      return;
    }
    
    // Use direct navigation instead of popup to avoid COOP blocking issues
    const googleAuthUrl = `${window.location.origin}/api/auth/google/login`;
    window.location.href = googleAuthUrl;
  };

  const handleAppleLogin = () => {
    alert('Apple Sign In coming soon!');
  };

  return (
    <div className="min-h-screen bg-[#0a0a0f] flex">
      {/* Left Side - Branding Image */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/30 via-teal-500/20 to-emerald-500/30" />
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-50"
          style={{ backgroundImage: "url('https://images.unsplash.com/photo-1582213782179-e0d53f98f2ca?w=1200&q=80')" }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0f] via-transparent to-transparent" />
        
        <div className="relative z-10 flex flex-col justify-between p-12 h-full">
          {/* Logo */}
          <Link to="/" className="inline-flex items-center gap-3">
            <div className="w-12 h-12 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-2xl flex items-center justify-center shadow-lg">
              <Heart className="w-6 h-6 text-white" />
            </div>
            <span className="text-2xl font-bold text-white">FundTheHope</span>
          </Link>

          {/* Content */}
          <div className="max-w-md">
            <h1 className="text-4xl font-bold text-white mb-4 leading-tight">
              Turn compassion<br />into action.
            </h1>
            <p className="text-lg text-white/70 mb-8">
              Join thousands of changemakers who are funding hope and transforming lives through the power of community.
            </p>
            
            <div className="flex items-center gap-4">
              <div className="flex -space-x-3">
                {[1,2,3,4,5].map(i => (
                  <img
                    key={i}
                    src={`https://i.pravatar.cc/40?img=${i+10}`}
                    alt="User"
                    className="w-10 h-10 rounded-full border-2 border-[#0a0a0f]"
                  />
                ))}
              </div>
              <div className="text-white/70 text-sm">
                <span className="text-white font-semibold">50,000+</span> people<br />already joined
              </div>
            </div>
          </div>

          {/* Stats */}
          <div className="flex gap-8">
            <div>
              <p className="text-3xl font-bold text-white">Growing</p>
              <p className="text-white/50 text-sm">Funds Raised</p>
            </div>
            <div>
              <p className="text-3xl font-bold text-white">2,500+</p>
              <p className="text-white/50 text-sm">Campaigns</p>
            </div>
            <div>
              <p className="text-3xl font-bold text-white">98%</p>
              <p className="text-white/50 text-sm">Success Rate</p>
            </div>
          </div>
        </div>
      </div>

      {/* Right Side - Auth Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-6 lg:p-12">
        <div className="w-full max-w-[420px]">
          {/* Mobile Logo */}
          <div className="lg:hidden text-center mb-8">
            <Link to="/" className="inline-flex items-center justify-center gap-3">
              <div className="w-12 h-12 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-2xl flex items-center justify-center">
                <Heart className="w-6 h-6 text-white" />
              </div>
              <span className="text-2xl font-bold text-white">FundTheHope</span>
            </Link>
          </div>

          {/* Card */}
          <div className="bg-card rounded-3xl p-8 shadow-2xl border border-border">
            {/* Tabs */}
            <div className="flex bg-secondary rounded-2xl p-1 mb-8">
              <button
                onClick={() => setActiveTab('signin')}
                className={`flex-1 py-3 px-4 rounded-xl text-sm font-semibold transition-all duration-200 ${
                  activeTab === 'signin'
                    ? 'bg-gradient-to-r from-emerald-500 to-teal-500 text-white shadow-lg'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {t('signIn')}
              </button>
              <button
                onClick={() => setActiveTab('signup')}
                className={`flex-1 py-3 px-4 rounded-xl text-sm font-semibold transition-all duration-200 ${
                  activeTab === 'signup'
                    ? 'bg-gradient-to-r from-emerald-500 to-teal-500 text-white shadow-lg'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {t('signUp')}
              </button>
            </div>

            {/* Social Login Icons */}
            <div className="flex justify-center gap-4 mb-6">
              {/* Google */}
              <button
                onClick={handleGoogleLogin}
                className={`relative w-14 h-14 rounded-full bg-white/5 border border-border flex items-center justify-center hover:bg-white/10 hover:border-white/20 transition-all duration-200 ${!googleEnabled ? 'opacity-60' : ''}`}
                title={googleEnabled ? 'Sign in with Google' : 'Google Sign In - Not Configured'}
              >
                <svg className="w-6 h-6" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
              </button>
            </div>

            {/* Divider */}
            <div className="relative mb-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-border"></div>
              </div>
              <div className="relative flex justify-center">
                <span className="px-4 text-sm text-muted-foreground bg-background">or continue with email</span>
              </div>
            </div>

            {/* Sign In Form */}
            {activeTab === 'signin' && (
              <form onSubmit={handleLogin} className="space-y-5">
                {loginError && (
                  <Alert variant="destructive" className="bg-red-500/10 border-red-500/20 text-red-400">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{loginError}</AlertDescription>
                  </Alert>
                )}

                <div>
                  <Label className="text-sm text-muted-foreground mb-2 block">{t('email') || 'Email'}</Label>
                  <div className="relative">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                    <Input
                      type="email"
                      placeholder={t('enterEmail') || 'Enter your email'}
                      value={loginEmail}
                      onChange={(e) => setLoginEmail(e.target.value)}
                      className="pl-12 h-14 bg-secondary border-border text-foreground placeholder:text-muted-foreground rounded-xl focus:border-emerald-500/50 focus:ring-emerald-500/20"
                      required
                    />
                  </div>
                </div>

                <div>
                  <div className="flex justify-between items-center mb-2">
                    <Label className="text-sm text-muted-foreground">{t('password') || 'Password'}</Label>
                    <Link to="/forgot-password" className="text-sm text-emerald-400 hover:text-emerald-300 transition-colors">
                      {t('forgotPassword') || 'Forgot?'}
                    </Link>
                  </div>
                  <div className="relative">
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      placeholder={t('enterPassword') || 'Enter your password'}
                      value={loginPassword}
                      onChange={(e) => setLoginPassword(e.target.value)}
                      className="pl-12 pr-12 h-14 bg-secondary border-border text-foreground placeholder:text-muted-foreground rounded-xl focus:border-emerald-500/50 focus:ring-emerald-500/20"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                <Button
                  type="submit"
                  className="w-full h-14 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-400 hover:to-teal-400 text-white font-semibold rounded-xl shadow-lg shadow-emerald-500/25 transition-all duration-200"
                  disabled={loginLoading}
                >
                  {loginLoading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    t('signIn')
                  )}
                </Button>

                {/* Sign Up Link */}
                <p className="text-center text-muted-foreground text-sm pt-2">
                  {t('dontHaveAccount') || "Don't have an account?"}{' '}
                  <button
                    type="button"
                    onClick={() => setActiveTab('signup')}
                    className="text-emerald-400 hover:text-emerald-300 font-medium transition-colors"
                  >
                    Sign up
                  </button>
                </p>
              </form>
            )}

            {/* Sign Up Form */}
            {activeTab === 'signup' && (
              <form onSubmit={handleSignup} className="space-y-4">
                {isEmailExists && (
                  <Alert className="bg-amber-500/10 border-amber-500/20 text-amber-400">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>
                      Email already registered.{' '}
                      <button
                        type="button"
                        onClick={() => setActiveTab('signin')}
                        className="text-emerald-400 hover:underline font-medium"
                      >
                        Sign in instead
                      </button>
                    </AlertDescription>
                  </Alert>
                )}

                {signupError && (
                  <Alert variant="destructive" className="bg-red-500/10 border-red-500/20 text-red-400">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{signupError}</AlertDescription>
                  </Alert>
                )}

                <div>
                  <Label className="text-sm text-muted-foreground mb-2 block">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                    <Input
                      type="email"
                      placeholder="you@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="pl-12 h-14 bg-secondary border-border text-foreground placeholder:text-muted-foreground rounded-xl focus:border-emerald-500/50 focus:ring-emerald-500/20"
                      required
                    />
                  </div>
                </div>

                <div>
                  <Label className="text-sm text-muted-foreground mb-2 block">Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Min. 6 characters"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="pl-12 pr-12 h-14 bg-secondary border-border text-foreground placeholder:text-muted-foreground rounded-xl focus:border-emerald-500/50 focus:ring-emerald-500/20"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                <div>
                  <Label className="text-sm text-muted-foreground mb-2 block">Confirm Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Confirm password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="pl-12 h-14 bg-secondary border-border text-foreground placeholder:text-muted-foreground rounded-xl focus:border-emerald-500/50 focus:ring-emerald-500/20"
                      required
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  className="w-full h-14 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-400 hover:to-teal-400 text-white font-semibold rounded-xl shadow-lg shadow-emerald-500/25 transition-all duration-200"
                  disabled={signupLoading}
                >
                  {signupLoading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    'Create Account'
                  )}
                </Button>

                {/* Sign In Link */}
                <p className="text-center text-muted-foreground text-sm pt-2">
                  Already have an account?{' '}
                  <button
                    type="button"
                    onClick={() => setActiveTab('signin')}
                    className="text-emerald-400 hover:text-emerald-300 font-medium transition-colors"
                  >
                    Sign in
                  </button>
                </p>
              </form>
            )}
          </div>

          {/* Footer */}
          <p className="text-center text-sm text-muted-foreground mt-6">
            By continuing, you agree to our{' '}
            <Link to="/terms" className="text-emerald-400 hover:text-emerald-300 transition-colors">Terms</Link>
            {' '}and{' '}
            <Link to="/privacy" className="text-emerald-400 hover:text-emerald-300 transition-colors">Privacy Policy</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default AuthPage;
